// Results screen (Screen 4) — PSD §10.2. 2 columns + below-fold tables + 4 accordions.

import { Crosshair, PlayCircle } from 'lucide-react';
import { useActiveValuation, useResult, useValidationIssues, useStore, useImpliedPriceCheck } from '../store/selectors';
import { HeadlineMetrics } from '../components/results/HeadlineMetrics';
import { PerShareByClassTable } from '../components/results/PerShareByClassTable';
import { AllocationTable } from '../components/results/AllocationTable';
import { Accordion } from '../components/results/Accordion';
import { SolverDiagnostics } from '../components/results/SolverDiagnostics';
import { BreakpointTable } from '../components/results/BreakpointTable';
import { GrantValidation } from '../components/results/GrantValidation';
import { ImpliedPriceCheck } from '../components/results/ImpliedPriceCheck';
import { EmptyState } from '../components/common/EmptyState';
import { LoadDemoButton } from '../components/common/LoadDemoButton';
import { ErrorState } from '../components/common/ErrorState';

export function Results() {
  const active = useActiveValuation();
  const result = useResult(active?.valuationId ?? null);
  const issues = useValidationIssues(active?.valuationId ?? null);
  const impliedCheck = useImpliedPriceCheck();
  const navigate = useStore((s) => s.navigate);

  if (!active) {
    return (
      <div>
        <h1 className="screen-title">Results</h1>
        <EmptyState
          icon={<Crosshair size={32} strokeWidth={1.5} />}
          message="No active valuation."
          hint="Results reflect the valuation selected in the top breadcrumb — none is active right now. Load a demo to see a fully-populated, converged result set instantly."
          action={<LoadDemoButton />}
        />
      </div>
    );
  }

  if (!result) {
    return (
      <div>
        <h1 className="screen-title">Results</h1>
        <EmptyState
          icon={<PlayCircle size={32} strokeWidth={1.5} />}
          message="Run the valuation to see results."
          hint="Headline FMV, the per-class allocation, and every table below are produced once the solver converges on the active valuation — it hasn't been run yet."
          action={
            <button type="button" className="btn btn-primary" onClick={() => navigate('builder')}>
              Go to Builder
            </button>
          }
        />
      </div>
    );
  }

  const nonConverged = !result.solver.converged || !result.reconciled;

  // Stage 6e /polish: break the uniform 2-col results-grid. The hero FMV now
  // sits open at the top of the page (no container); supporting data flows —
  // per-share table, implied-price check, accordions, allocation table —
  // stack below as proper cards with generous inter-section rhythm. Not every
  // panel needs a border: the hero reads as the page's primary signal.
  return (
    <div>
      <h1 className="screen-title">Results</h1>
      {nonConverged ? (
        <ErrorState
          message="The solver did not converge within tolerance, or allocations do not reconcile."
          detail={`Converged: ${result.solver.converged} · Reconciled: ${result.reconciled} · Residual: ${result.solver.residualError.toFixed(6)}`}
          action={
            <button type="button" className="btn btn-secondary" onClick={() => navigate('builder')}>
              Review inputs &amp; re-run
            </button>
          }
        />
      ) : (
        <div className="section-group results-reveal">
          <HeadlineMetrics headline={result.headline} />
          <PerShareByClassTable classes={result.headline.perShareByClass} />
          {impliedCheck && impliedCheck.length > 0 && <ImpliedPriceCheck rows={impliedCheck} />}
          <div>
            <Accordion
              title="Solver Diagnostics"
              badge={<span className="badge badge-info">F2.5</span>}
              defaultOpen={!result.solver.converged}
            >
              <SolverDiagnostics diagnostics={result.solver} capTable={result.inputs.capTableSnapshot} />
            </Accordion>
            <Accordion title="Grant Validation" badge={<span className="badge badge-warning">F9.6</span>}>
              <GrantValidation headline={result.headline} valuationDate={result.inputs.compliance.valuationDate} />
            </Accordion>
            <Accordion title="Breakpoint Table" badge={<span className="badge badge-info">F8.7</span>}>
              <BreakpointTable breakpoints={result.breakpoints} allocation={result.allocation} />
            </Accordion>
            <Accordion title="Validation Issues">
              {issues.length === 0 ? (
                <p>No validation issues.</p>
              ) : (
                <ul>
                  {issues.map((i, idx) => (
                    <li key={idx}>
                      <strong className={`badge badge-${i.severity === 'error' ? 'error' : 'warning'}`}>
                        {i.code}
                      </strong>{' '}
                      {i.message}
                    </li>
                  ))}
                </ul>
              )}
            </Accordion>
          </div>
          <AllocationTable allocation={result.allocation} />
        </div>
      )}
    </div>
  );
}
